import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { defaultStates } from '@/modules/tasks/taskboard/kanban/constants';
import TaskRow from '@/modules/tasks/taskboard/kanban/task/TaskRow';
import SortTaskComponent from '@/modules/tasks/taskboard/partial/SortTaskComponent';
import taskboardMessages from '@/modules/tasks/taskboard/taskboardMessages';
import TaskContainer from '@/modules/tasks/taskboard/TaskContainer';
import taskMessages from '@/modules/tasks/taskMessages';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import { injectStateService } from '@/shared/services/StateService';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import omit from 'lodash-es/omit';
import isEqual from 'lodash-es/isEqual';
import SignalRService from '@/shared/services/SignalRService';
import { useGetPlannedTasks } from '@/shared/hooks/task/useGetPlannedTasks';
import { getLocalStorageItem, LocalStorageKey, setLocalStorageItem, } from '@/shared/services/localStorageService';
import { useQueryClient } from '@tanstack/react-query';
import { TaskQueryKey } from '@/shared/hooks/task/queryKey';
import { ModalType, useModalContext } from '@/shared/providers/ModalProvider';
import { showWhenPermission } from '@/shared/components/security';
import { useGetKPIs } from '@/shared/hooks/task/useGetKPIs';
import { useTaskTimerContext } from '@/shared/providers/TaskTimerProvider';
import { TaskBoardFilters } from './partial/filters/TaskBoardHeader';
import { migrateTaskboardFilters, } from './constants';
import { functionalities, privileges } from '@/shared/services/AuthService';
const TaskboardContainer = props => {
    var _a;
    const { formatMessage } = useIntl();
    const { setShowModalInfo } = useModalContext();
    const [openTaskDetailForTaskInRoute, setOpenTaskDetailForTaskInRoute] = useState(true);
    const stateProps = omit(props, ['dataCache', 'stateService']);
    const initialSearchParams = {
        page: 0,
        pageSize: 50,
        sortKey: 'relation',
        desc: false,
        width: '25vw',
    };
    const queryClient = useQueryClient();
    const handleTaskHasAttachmentsMessage = message => {
        var _a;
        if (message.level === 2)
            return; // ignore todo's
        let parentId = null;
        const previousTasks = queryClient.getQueryData([TaskQueryKey.PlannedTasks]);
        const updatedTasksQueryData = Object.assign({}, previousTasks);
        const mainTasks = (_a = updatedTasksQueryData === null || updatedTasksQueryData === void 0 ? void 0 : updatedTasksQueryData.pages) === null || _a === void 0 ? void 0 : _a.flat(1);
        const isMainTask = message.level === 0;
        const mainTask = mainTasks.find(x => { var _a; return x.id === ((_a = message.parentId) !== null && _a !== void 0 ? _a : message.id); });
        if (!mainTask)
            return;
        if (isMainTask) {
            mainTask.hasAttachments = message.hasAttachments;
        }
        else {
            const { children } = mainTask;
            const subTask = children.find(x => x.id === message.id);
            if (subTask) {
                parentId = mainTask.id;
                subTask.hasAttachments = message.hasAttachments;
                mainTask.hasAttachments = message.parentHasAttachments;
                mainTask.children = [...children]; // useEffect in KanbanBoard.tsx is not called without this line, and we need it to trigger the reshuffling of columns
            }
        }
        if (parentId) {
            queryClient.invalidateQueries([TaskQueryKey.TaskAttachments, parentId]);
        }
        queryClient.invalidateQueries([TaskQueryKey.TaskAttachments, message.id]);
        queryClient.setQueryData([TaskQueryKey.PlannedTasks], updatedTasksQueryData);
    };
    // if the route contains a task ID, from either the running task clock icon or the 'share task' link, we want to open the task detail modal of this task. Except for when the user navigated from task overview to taskboard..
    useEffect(() => {
        const { plannedTaskId, createTaskForRelation = null } = stateProps;
        if (plannedTaskId && openTaskDetailForTaskInRoute && stateProps.openPlannedTaskDetailModal) {
            setShowModalInfo({
                modalType: ModalType.TaskDetail,
                task: { id: stateProps.plannedTaskId },
            });
            setOpenTaskDetailForTaskInRoute(false);
        }
        else if (openTaskDetailForTaskInRoute && createTaskForRelation) {
            setShowModalInfo({
                modalType: ModalType.OneTimeTask,
                relationId: createTaskForRelation,
            });
            setOpenTaskDetailForTaskInRoute(false);
        }
        SignalRService.subscribe('taskHasAttachmentsChanged', handleTaskHasAttachmentsMessage);
        return () => {
            SignalRService.unsubscribe('taskHasAttachmentsChanged', handleTaskHasAttachmentsMessage);
        };
    }, [stateProps, setShowModalInfo, openTaskDetailForTaskInRoute, setOpenTaskDetailForTaskInRoute]);
    const getInitialOpenTaskIds = () => {
        if (stateProps.plannedTaskId) {
            return [stateProps.plannedTaskId];
        }
        return getLocalStorageItem(LocalStorageKey.TaskBoardContainer_OpenTaskIdsArray, []);
    };
    const [openTaskIds, setOpenTaskIds] = useState(getInitialOpenTaskIds());
    const [useParametersFromRoute, setUseParametersFromRoute] = useState(true);
    const setOpenTasks = (newValue) => {
        setLocalStorageItem(LocalStorageKey.TaskBoardContainer_OpenTaskIdsArray, newValue);
        setOpenTaskIds(newValue);
    };
    const getFiltersFromRouteOrLocalStorage = () => {
        const emptyState = {
            invoiceStreamId: null,
            relation: null,
            taskTemplateId: null,
            plannedTaskId: null,
            kpi: null,
            userId: null,
            onlySubtasksForAssignedUser: null,
            userIsNextAssignedUser: null,
            openPlannedTaskDetailModal: true,
            deadlineFrom: null,
            deadlineUntil: null,
            createTaskForRelation: null,
        };
        if (!isEqual(stateProps, emptyState) && useParametersFromRoute) {
            let filterFromState = {};
            if (stateProps.relation) {
                filterFromState.relationId = stateProps.relation.id;
                filterFromState.relationName = stateProps.relation.name;
                delete stateProps.relation;
            }
            if (stateProps.taskTemplateId) {
                filterFromState.taskTemplates = [stateProps.taskTemplateId];
                delete stateProps.taskTemplateId;
            }
            filterFromState = Object.assign(Object.assign({}, filterFromState), stateProps);
            if (stateProps.plannedTaskId) {
                setOpenTasks([stateProps.plannedTaskId]);
            }
            sessionStorage.setItem('TaskboardParams', JSON.stringify(Object.assign({}, filterFromState)));
            // we only want to use the route parameters the first time this method is called, otherwise resetting the filters does not work
            setUseParametersFromRoute(false);
            return filterFromState;
        }
        const storageString = sessionStorage.getItem('TaskboardParams');
        if (!storageString) {
            return undefined;
        }
        try {
            const filterObject = JSON.parse(storageString);
            return filterObject;
        }
        catch (error) {
            console.log(error);
            return {};
        }
    };
    const [activeFilters, setActiveFilters] = useState((_a = getFiltersFromRouteOrLocalStorage()) !== null && _a !== void 0 ? _a : {});
    const { data, isLoading, refetch: onRefetch, fetchNextPage } = useGetPlannedTasks(activeFilters, {
        page: 0,
        take: initialSearchParams.pageSize,
    });
    const { data: kpiData } = useGetKPIs(activeFilters);
    const refetch = () => {
        if (isLoading) {
            const queryKey = [TaskQueryKey.PlannedTasks];
            queryClient.cancelQueries(queryKey);
            queryClient.invalidateQueries({ queryKey });
        }
        return onRefetch();
    };
    const loadItems = async () => {
        var _a;
        const result = await fetchNextPage();
        return (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.pages[result.data.pages.length - 1];
    };
    const { setTasks } = useTaskTimerContext();
    useEffect(() => {
        if (data) {
            setTasks(data);
        }
    }, [data, setTasks]);
    const hasOpenTasks = (openTaskIds === null || openTaskIds === void 0 ? void 0 : openTaskIds.length) >= 1;
    const onSetIsAllCollapsed = () => {
        if (hasOpenTasks) {
            setOpenTasks([]);
        }
        else {
            setOpenTasks(data === null || data === void 0 ? void 0 : data.map(m => m.id));
        }
    };
    const onSetIsCollapsed = (task) => {
        let copy = [...openTaskIds];
        const index = copy.findIndex(x => x === task.id);
        const subtaskIds = task.children ? task.children.map(x => x.id) : [];
        // openTaskIds can contain a subtask id (if navigated from other screen and we want to open this task when navigation to taskboard)
        const taskIsUncollapsedBySubtask = task.children && subtaskIds.some(x => openTaskIds.includes(x));
        if (index >= 0) {
            copy.splice(index, 1);
        }
        else if (!taskIsUncollapsedBySubtask) {
            copy.push(task.id);
        }
        if (taskIsUncollapsedBySubtask) {
            copy = copy.filter(x => !subtaskIds.some(y => x === y));
        }
        setOpenTasks(copy);
    };
    const onCreateNewTask = () => {
        setShowModalInfo({
            modalType: ModalType.OneTimeTask,
            relationId: activeFilters.relationId,
        });
    };
    const renderRow = (task) => {
        const subTaskIds = task.children ? task.children.map(x => x.id) : [];
        const isCollapsed = !openTaskIds.includes(task.id) && !openTaskIds.some(r => subTaskIds.includes(r));
        return (_jsx(TaskRow, { task: task, isCollapsed: isCollapsed, onSetIsCollapsed: onSetIsCollapsed }, task.id + isCollapsed));
    };
    const setFilter = (filter) => {
        setActiveFilters(prev => {
            const newFilter = migrateTaskboardFilters(Object.assign(Object.assign({}, prev), filter));
            sessionStorage.setItem('TaskboardParams', JSON.stringify(newFilter));
            return newFilter;
        });
        setTimeout(() => {
            refetch();
        }, 0);
    };
    return (_jsxs(_Fragment, { children: [_jsx(TaskBoardFilters, { activeFilters: activeFilters, setFilter: setFilter }), _jsxs(LoadingContainer, Object.assign({ isLoading: isLoading && !data }, { children: [_jsx("div", Object.assign({ className: "container-fluid" }, { children: _jsx("div", Object.assign({ className: "row btn-row" }, { children: kpiData && (_jsxs("span", Object.assign({ className: "right" }, { children: [data === null || data === void 0 ? void 0 : data.length, " / ", kpiData === null || kpiData === void 0 ? void 0 : kpiData.total] }))) })) })), _jsx(TaskContainer, { children: _jsxs(ScrollableTable, Object.assign({ tableClassName: "width100pct dskb-layout", loadItems: loadItems, initialSearchParams: initialSearchParams, items: data !== null && data !== void 0 ? data : [], renderRow: item => renderRow(item), noDataLabel: _jsx(FormattedMessage, Object.assign({}, taskMessages.NoTasksFound)), fetchOnMount: false, renderTBody: true, noHover: true }, { children: [_jsx(ColumnHeader, { title: "" }, "placeholder"), _jsx(ColumnHeader, { title: _jsx(SortTaskComponent, { title: formatMessage(hasOpenTasks ? taskboardMessages.CollapseAll : taskboardMessages.ExpandAll), hasOpenTasks: hasOpenTasks, toggleOpenTasks: onSetIsAllCollapsed }) }, "isAllCollapsed"), defaultStates.map(defaultState => (_jsx(ColumnHeader, { title: formatMessage(listItemMsg('taskStatus', defaultState.status)) }, defaultState.name)))] })) })] }))] }));
};
export default showWhenPermission(functionalities.PlannedTask, privileges.Read)(injectStateService(TaskboardContainer));
